import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLanguages, setShowLanguages] = useState(false);
  const [showMediaDropdown, setShowMediaDropdown] = useState(false);
  const { language, setLanguage, t, languages } = useLanguage();
  const location = useLocation();

  const navItems = [
    { path: '/', label: t.home },
    { path: '/about', label: t.about },
    { path: '/services', label: t.services },
    { path: '/investment-guide', label: t.investmentGuide },
    { path: '/company-formation', label: t.companyFormation }
  ];

  const mediaItems = [
    { path: '/videos', label: t.videos || (language === 'fr' ? 'Vidéos' : 'Videos') },
    { path: '/projects', label: t.projects || (language === 'fr' ? 'Projets' : 'Projects') }
  ];

  const bottomNavItems = [
    { path: '/news', label: t.news },
    { path: '/contact', label: t.contact }
  ];

  const isActive = (path) => location.pathname === path;
  const isMediaActive = () => location.pathname === '/videos' || location.pathname === '/projects';

  return (
    <nav className="bg-card border-b border-border sticky top-0 z-40 backdrop-blur-lg bg-card/95" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <div className="h-14 w-14 rounded-full bg-white p-2 shadow-md border-2 border-primary/20">
              <img 
                src="/fbg-logo.png" 
                alt="First Business Group" 
                className="h-full w-full object-contain"
              />
            </div>
            <div>
              <h1 className="font-serif text-xl font-bold text-foreground tracking-tight">First Business Group</h1>
              <p className="text-xs text-muted-foreground">S.A.R.L.U. - DRC Investment Experts</p>
            </div>
          </Link>

          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-primary'
                }`}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Media Dropdown - Premium Design */}
            <div 
              className="relative group"
              onMouseEnter={() => setShowMediaDropdown(true)}
              onMouseLeave={() => setShowMediaDropdown(false)}
            >
              <button
                data-testid="nav-media-dropdown"
                className={`px-4 py-2 text-sm font-medium transition-all duration-300 flex items-center space-x-2 relative ${
                  isMediaActive()
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-primary'
                }`}
              >
                <span>{language === 'fr' ? 'Média' : 'Media'}</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${showMediaDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {showMediaDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="absolute left-0 mt-2 w-64 bg-card border border-border rounded-xl shadow-2xl overflow-hidden z-50"
                    style={{
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.05)'
                    }}
                    data-testid="media-dropdown-menu"
                  >
                    <div className="relative">
                      {/* Gradient header */}
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-secondary to-primary"></div>
                      
                      <div className="py-2 px-1">
                        {mediaItems.map((item, idx) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            data-testid={`dropdown-${item.path.replace('/', '')}`}
                            className="relative"
                          >
                            <motion.div
                              initial={{ x: -20, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              transition={{ delay: idx * 0.05 }}
                              className={`group/item flex items-center space-x-3 px-4 py-3 mx-1 rounded-lg transition-all duration-200 ${
                                isActive(item.path)
                                  ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-md'
                                  : 'text-foreground hover:bg-muted hover:translate-x-1'
                              }`}
                            >
                              {/* Icon */}
                              <div className={`flex-shrink-0 transition-transform duration-200 group-hover/item:scale-110 ${
                                isActive(item.path) ? 'text-white' : 'text-secondary'
                              }`}>
                                {item.path === '/videos' ? (
                                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                ) : (
                                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                  </svg>
                                )}
                              </div>
                              
                              {/* Text */}
                              <div className="flex-1">
                                <div className={`font-medium text-sm ${
                                  isActive(item.path) ? 'text-white' : 'text-foreground group-hover/item:text-primary'
                                }`}>
                                  {item.label}
                                </div>
                                <div className={`text-xs mt-0.5 ${
                                  isActive(item.path) ? 'text-white/80' : 'text-muted-foreground'
                                }`}>
                                  {item.path === '/videos' 
                                    ? (language === 'fr' ? 'Présentations' : 'Presentations')
                                    : (language === 'fr' ? 'En cours' : 'In progress')
                                  }
                                </div>
                              </div>
                              
                              {/* Arrow indicator */}
                              <svg 
                                className={`h-4 w-4 transition-all duration-200 ${
                                  isActive(item.path) 
                                    ? 'text-white opacity-100' 
                                    : 'text-primary opacity-0 group-hover/item:opacity-100 group-hover/item:translate-x-1'
                                }`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </motion.div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {bottomNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-primary'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center space-x-4">
            <div className="relative">
              <button
                data-testid="language-selector"
                onClick={() => setShowLanguages(!showLanguages)}
                className="flex items-center space-x-2 px-4 py-2 rounded-full border border-border hover:border-primary transition-colors"
              >
                <Globe className="h-4 w-4" />
                <span className="text-sm font-medium">{languages[language]}</span>
              </button>

              <AnimatePresence>
                {showLanguages && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-xl p-2"
                    data-testid="language-dropdown"
                  >
                    {Object.entries(languages).map(([code, name]) => (
                      <button
                        key={code}
                        data-testid={`lang-${code}`}
                        onClick={() => {
                          setLanguage(code);
                          setShowLanguages(false);
                        }}
                        className={`w-full text-left px-4 py-2 rounded-md text-sm transition-colors ${
                          language === code
                            ? 'bg-primary text-white'
                            : 'hover:bg-muted'
                        }`}
                      >
                        {name}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-md text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-border bg-card overflow-y-auto max-h-[calc(100vh-80px)]"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 space-y-2">
              {/* Main Nav Items */}
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary text-white'
                      : 'text-muted-foreground hover:bg-muted hover:text-primary'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              
              {/* Media Section */}
              <div className="pt-2">
                <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {language === 'fr' ? 'Média' : 'Media'}
                </p>
                {mediaItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-primary text-white'
                        : 'text-muted-foreground hover:bg-muted hover:text-primary'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              {/* Bottom Nav Items (News, Contact) */}
              <div className="pt-2">
                {bottomNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-primary text-white'
                        : 'text-muted-foreground hover:bg-muted hover:text-primary'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
              
              {/* Language Selector */}
              <div className="pt-4 border-t border-border">
                <p className="px-4 py-2 text-xs font-medium text-muted-foreground">Language / Langue</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(languages).map(([code, name]) => (
                    <button
                      key={code}
                      onClick={() => {
                        setLanguage(code);
                        setIsOpen(false);
                      }}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        language === code
                          ? 'bg-secondary text-white'
                          : 'bg-muted text-foreground hover:bg-muted/80'
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;